import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Book } from '../../types';
import { getGraphQLClient, GET_BOOK } from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { Drawer, Spinner } from '../common';
import StorytellerTab from '../wizard/StorytellerTab';

interface VoiceDrawerProps {
  bookId: string;
  isOpen: boolean;
  onClose: () => void;
}

export default function VoiceDrawer({ bookId, isOpen, onClose }: VoiceDrawerProps) {
  const { t } = useTranslation();
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const client = getGraphQLClient();
        const data = await client.request<{ book: Book | null }>(GET_BOOK, { id: bookId });
        if (cancelled) return;
        if (data?.book) {
          setBook(data.book);
        } else {
          setError(t('common.error', 'Something went wrong'));
        }
      } catch (err) {
        captureException(err);
        if (!cancelled) setError(t('common.error', 'Something went wrong'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [bookId, isOpen, t]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('voiceDrawer.title', 'Voice & writing style')}
      subtitle={t('voiceDrawer.subtitle', 'How the AI should sound')}
      width="lg"
    >
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Spinner />
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {!loading && !error && book && (
        <div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t(
              'voiceDrawer.description',
              'Describe the narrative voice — pacing, sentence rhythm, vocabulary. This shapes every scene the AI drafts.'
            )}
          </p>
          <StorytellerTab book={book} onBookChange={setBook} onNext={onClose} />
        </div>
      )}
    </Drawer>
  );
}
